import { useState } from "react";
import { useDispatch } from "react-redux";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { ConditionalLink } from "components/conditionalLink";
import { Calculator } from "components/icons/LeftNav/SalesTax/Calculator";
import { SmallArrowUpRight } from "components/icons/SmallArrowUpRight";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useCreateMessageDraftMutation } from "store/apis/chat";
import { useGetEntityProfitabilityQuery } from "store/apis/financialMetrics";
import { openFloatingChat, setToOpenChatId } from "store/slices/chat";

const ESTIMATED_TAX_DRAFT_MESSAGE =
  "Hi team, I wanted to reach out and see if we need to take any action on estimated taxes.";

export const EstimatedTaxNudgeBanner = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const entityId = useCurrentEntityId();
  const { uuid: groupId, secondary_channel_url } = useCurrentGroupContext();
  const { isAdmin } = useRoleBasedView();
  const [isDismissed, setIsDismissed] = useState(false);
  const [createMessageDraft] = useCreateMessageDraftMutation();

  const { data } = useGetEntityProfitabilityQuery(
    { entityId },
    { skip: !entityId }
  );

  const advanceTaxPath = isAdmin
    ? `/admin/crm/${groupId}/advance-tax?entity=${entityId}`
    : `/tax/advance-tax?entity=${entityId}`;

  const handleTalkToTeam = async () => {
    if (!secondary_channel_url) return;
    try {
      await createMessageDraft({
        channelUrl: secondary_channel_url,
        message: ESTIMATED_TAX_DRAFT_MESSAGE,
      }).unwrap();
    } catch {
      toast.error({
        message: "Couldn't pre-fill your message, but you can still type it",
      });
    }
    dispatch(setToOpenChatId(secondary_channel_url));
    dispatch(openFloatingChat());
  };

  if (!data?.is_profitable || isDismissed) return null;

  return (
    <Banner
      type="info"
      icon={
        <span className="t-flex t-shrink-0 t-self-start t-text-blue-70">
          <Calculator />
        </span>
      }
      title="Estimated taxes may apply"
      description="Our analysis suggests your company may be profitable this year. Make estimated payments to the IRS to avoid underpayment penalties and interest."
      onClose={() => setIsDismissed(true)}
      cta={
        <div className="t-flex t-items-center t-gap-4">
          <ConditionalLink to={advanceTaxPath}>
            <Button customType="secondary" size="small">
              <span className="t-flex t-items-center t-gap-1">
                Show details
                <span className="t-flex t-h-2 t-w-2 t-overflow-hidden t-text-text-30">
                  <SmallArrowUpRight className="t-h-2 t-w-2" />
                </span>
              </span>
            </Button>
          </ConditionalLink>
          <Button
            customType="link_neutral"
            size="small"
            onClick={() => void handleTalkToTeam()}
          >
            Talk to support
          </Button>
        </div>
      }
    />
  );
};
